import { useMemo, useRef, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { expVal } from '@confluence/feature-experiments';

interface UseEnrolledAIInEditorModalsExperimentProps {
	touchpoint: 'ai-in-editor' | 'end-of-trial';
	isEligible: boolean;
	extraAnalyticsAttributes?: object;
	trialDaysLeft?: number;
}

export const useEnrolledAIInEditorModalsExperiment = ({
	touchpoint,
	isEligible,
	extraAnalyticsAttributes = {},
	trialDaysLeft,
}: UseEnrolledAIInEditorModalsExperimentProps) => {
	const memoizedAnalyticsAttributes = useMemo(
		() => extraAnalyticsAttributes,
		[extraAnalyticsAttributes],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredEventsRef = useRef<{
		aa: Set<string>;
		ab: Set<string>;
	}>({
		aa: new Set(),
		ab: new Set(),
	});

	const experimentValues = useMemo(() => {
		if (!isEligible) {
			return {
				isABTreatment: false,
			};
		}

		if (
			expVal('twcg-530_confluence_ai_in_editor_eot', 'cohort', 'not-enrolled') !== 'not-enrolled'
		) {
			return {
				isABTreatment:
					expVal('twcg-530_confluence_ai_in_editor_eot', 'cohort', 'not-enrolled') === 'treatment',
			};
		}

		return {
			isABTreatment: false,
		};
	}, [isEligible]);

	useEffect(() => {
		if (!isEligible) {
			return;
		}

		if (
			!hasFiredEventsRef.current.aa.has(touchpoint) &&
			expVal('twcg-530_confluence_ai_in_editor_eot_aa', 'cohort', 'not-enrolled') !== 'not-enrolled'
		) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'experiment',
					actionSubjectId: `twcg-530_confluence_ai_in_editor_eot_aa-exposed`,
					attributes: {
						cohort: expVal('twcg-530_confluence_ai_in_editor_eot_aa', 'cohort', 'not-enrolled'),
						touchpoint,
						trialDaysLeft,
						...memoizedAnalyticsAttributes,
					},
					source: 'twcg-530_confluence_ai_in_editor_eot_aa',
				},
			}).fire();
			hasFiredEventsRef.current.aa.add(touchpoint);
		}

		if (
			!hasFiredEventsRef.current.ab.has(touchpoint) &&
			expVal('twcg-530_confluence_ai_in_editor_eot', 'cohort', 'not-enrolled') !== 'not-enrolled'
		) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'experiment',
					actionSubjectId: `twcg-530_confluence_ai_in_editor_eot-exposed`,
					attributes: {
						cohort: expVal('twcg-530_confluence_ai_in_editor_eot', 'cohort', 'not-enrolled'),
						touchpoint,
						trialDaysLeft,
						...memoizedAnalyticsAttributes,
					},
					source: 'twcg-530_confluence_ai_in_editor_eot',
				},
			}).fire();
			hasFiredEventsRef.current.ab.add(touchpoint);
		}
	}, [isEligible, touchpoint, memoizedAnalyticsAttributes, createAnalyticsEvent, trialDaysLeft]);

	return experimentValues;
};
